import React, { useState, useEffect } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import PropertiesService from '../../Services/PropertiesService';
import "./MergeAllDocuments.css";

const MergeAllDocuments = ({ property = {}, ErrorForMergeDocument }) => {
  const [pdfUrls, setPdfUrls] = useState([]);
  const [completedDocs, setCompletedDocs] = useState([]);
  const [isMerging, setIsMerging] = useState(false);
  const [status, setStatus] = useState('');
  const [finalPdfUrl, setFinalPdfUrl] = useState(null);
  const [showProceedMerge, setShowProceedMerge] = useState(false);
  const [loadingProperty, setLoadingProperty] = useState(true);
  const [finalUploading, setFinalUploading] = useState(false);
  const [missingDocs, setMissingDocs] = useState([]);
  const [autoMerged, setAutoMerged] = useState(false);

  // ✅ You can set password here
  const PDF_PASSWORD = property?.propertyUniqueId || "docity@123";
  // You can use mobile/email/unique id etc.

  const REQUIRED_DOCS = [
    "⁠link documents explaining the title deeds?",
    "⁠Revenue department links?",
    "Advance receipt or any other proof of purchase?",
    "Property ownership details like previous Sale deed registered with seller?",
  ];

  const getFilenameFromUrl = (url) => {
    const parts = url?.split('/');
    return decodeURIComponent(parts?.[parts.length - 1].replace(/\+/g, ' '));
  };

  const fetchPdfThroughProxy = async (url) => {
    const proxyUrl = `https://api.docity.co/proxy-pdf?url=${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    return new Uint8Array(await response.arrayBuffer());
  };


  const mergePDFs = async () => {
    setIsMerging(true);
    setStatus('Starting merge...');
    setCompletedDocs([]);

    try {
      const mergedPdf = await PDFDocument.create();
      const completedFilenames = [];

      for (let i = 0; i < pdfUrls.length; i++) {
        const url = pdfUrls[i];
        const filename = getFilenameFromUrl(url);
        setStatus(`Processing ${filename} (${i + 1}/${pdfUrls.length})`);

        try {
          const pdfBytes = await fetchPdfThroughProxy(url);
          const pdf = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());

          // Visual tag logic remains same...
          const firstPage = copiedPages[0];
          if (firstPage) {
            const { height } = firstPage.getSize();
            firstPage.drawText(`Document: ${filename}`, {
              x: 50, y: height - 50, size: 14, color: rgb(0.2, 0.2, 0.8),
            });
          }

          copiedPages.forEach((page) => mergedPdf.addPage(page));
          completedFilenames.push(filename);
        } catch (err) {
          console.error(`Error processing ${filename}:`, err);
          const errorPage = mergedPdf.addPage();
          errorPage.drawText(`Could not load: ${filename}`, { x: 50, y: 500, size: 16, color: rgb(0.8, 0, 0) });
        }
      }

      setCompletedDocs(completedFilenames);

      // IMPORTANT: Removed mergedPdf.encrypt() as it doesn't exist in pdf-lib
      setStatus('Saving PDF...');

      const mergedPdfBytes = await mergedPdf.save();

      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const finalUrl = URL.createObjectURL(blob);

      setFinalPdfUrl(finalUrl);
      await uploadObjectUrlToS3(finalUrl);

      setStatus('PDF successfully merged!');
    } catch (err) {
      console.error(err);
      setStatus(`Error: ${err.message}`);
    } finally {
      setIsMerging(false);
    }
  };

  const uploadObjectUrlToS3 = async (objectUrl) => {
    try {
      setFinalUploading(true);
      const response = await fetch(objectUrl);
      if (!response.ok) throw new Error("Failed to fetch Blob from object URL");
      const blob = await response.blob();

      const formData = new FormData();
      formData.append("image", blob, "merged-document.pdf");

      const data = await PropertiesService.UploadFiles(formData);
      if (!data?.Location) throw new Error("S3 upload failed");

      const updatePayload = {
        userId: property?._id,
        docType: 'FinalPropertyDocument',
        docUrl: data?.Location,
      };

      const updateResp = await PropertiesService.uploadDoc(updatePayload);

      await PropertiesService.UpdateProperty({
        propertyId: property?._id,
        finalMergedWithAllDocuments: updateResp?.data?.url || "",
      });

      setFinalUploading(false);
    } catch (err) {
      console.error("Upload error:", err);
      setFinalUploading(false);
    }
  };

  const getProperty = async () => {
    setLoadingProperty(true);

    try {
      const propResp = await PropertiesService.getSignleProperty({ propertyId: property?._id });
      const docsResp = await PropertiesService.getDocs({ userId: propResp?.data?._id });

      const missing = REQUIRED_DOCS.filter(req =>
        !docsResp?.data?.some(doc => doc.Type?.trim() === req && doc.url && doc.url !== "No")
      );

      const pdfDocs = docsResp?.data?.filter(x => x.url?.startsWith('https')).map(x => x.url);

      setMissingDocs(missing);
      setPdfUrls(pdfDocs || []);

      if (pdfDocs?.length > 0 && missing.length === 0 && !propResp?.data?.finalMergedWithAllDocuments) {
        setShowProceedMerge(true);
      } else {
        setFinalPdfUrl(propResp?.data?.finalMergedWithAllDocuments || null);
        setShowProceedMerge(false);
      }
    } catch (err) {
      console.error("Error fetching property:", err);
    } finally {
      setLoadingProperty(false);
    }
  };

  useEffect(() => {
    if (property?._id) getProperty();
  }, [property]);

  useEffect(() => {
    if (pdfUrls?.length > 0 && showProceedMerge && !autoMerged) {
      mergePDFs();
      setAutoMerged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrls, showProceedMerge]);

  const handleDownload = () => {
    if (finalPdfUrl) {
      window.open(finalPdfUrl, '_blank');
      const link = document.createElement('a');
      link.href = finalPdfUrl;
      link.target = '_blank';
      link.rel = 'noopener,noreferrer';
      link.download = 'merged-documents.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="merge-wrapper">
      <div className="merge-card">
        {isMerging && (
          <div className="merge-section">
            <div className="spinner" />
            <p className="merge-hint">{status}</p>
          </div>
        )}

        {!isMerging && missingDocs.length > 0 && (
          <div className="status-box status-error">
            <p className="error-text">
              ❌ Documents incomplete. Please upload the following files:
              <br />
              {missingDocs.map((doc, idx) => (
                <span key={idx}>{idx + 1}. {doc} <br /></span>
              ))}
            </p>
          </div>
        )}

        {pdfUrls.length > 0 && isMerging && (
          <div className="doc-list">
            {pdfUrls.map((url, idx) => {
              const filename = getFilenameFromUrl(url);
              const isDone = completedDocs.includes(filename);
              return (
                <div key={idx} className="doc-card">
                  <span className="doc-name">📄 {filename}</span>
                  <span className={`doc-status ${isDone ? 'done' : 'pending'}`}>
                    {isDone ? '✔ Completed' : '⏳ Processing'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {!isMerging && finalPdfUrl && !finalUploading && (
          <div className="download-card">
            <p className="download-title">📄 Final Merged Document Ready</p>
            <button className="download-btn" onClick={handleDownload}>
              View & Download
            </button>


          </div>
        )}

        {finalUploading && (
          <div className="merge-section">
            <div className="spinner" />
            <p className="merge-hint">Uploading final document, please wait...</p>
          </div>
        )}

        {ErrorForMergeDocument && ErrorForMergeDocument !== 'Proceedt' && (
          <div className="status-box status-error">
            <p className="error-heading">Error:</p>
            <div className="error-text" style={{ display: "flex", alignItems: "center", color: "var(--color-oxford-blue)" }}>
              {ErrorForMergeDocument}
              <svg
                className="error-arrow-shake"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                style={{ marginLeft: "8px" }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M5 12h14M13 18l6-6-6-6" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergeAllDocuments;
